import React, { useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

// Custom Tooltip Component
const CustomTooltip = ({ active, payload, label, responses, dayDate }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const count = data.count;
    const itemId = data.id;
    
    // For textentry items, find and display the actual text response
    if (itemId.startsWith('textentry_')) {
      // Find the text response for this item on this day
      let textResponse = '';
      
      responses.forEach(response => {
        // Check if response is from the correct day and item
        const responseDate = response.session_id.split('_')[1];
        if (responseDate === dayDate && response.item_name === itemId && response.item_resp) {
          textResponse = response.item_resp;
        }
      });
      
      return (
        <div style={{ 
          backgroundColor: 'white', 
          padding: '10px', 
          border: '1px solid #ccc',
          borderRadius: '4px'
        }}>
          <p style={{ fontWeight: 'bold', margin: 0 }}>{label}</p>
          <p style={{ margin: '5px 0 0 0', color: '#333' }}>
            Response: {textResponse || 'Keine Texteingabe'}
          </p>
        </div>
      );
    }
    
    // For non-textentry items, show the count as before
    return (
      <div style={{ 
        backgroundColor: 'white', 
        padding: '10px', 
        border: '1px solid #ccc',
        borderRadius: '4px'
      }}>
        <p style={{ fontWeight: 'bold', margin: 0 }}>{label}</p>
        <p style={{ margin: '5px 0 0 0', color: '#333' }}>
          Anzahl: {count}
        </p>
      </div>
    );
  }
  return null;
};

const DayCategoryDetailModal = ({ isOpen, onClose, dayData, category, responses }) => {
  // Close modal on Escape key press
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.keyCode === 27) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Define subcategories for each main category
  const subcategories = {
    Auditiv: {
      'ba_aud_01_1': 'Geräusche',
      'ba_aud_01_2': 'Lautstärke', 
      'ba_aud_01_3': 'Töne von Geräten',
      'textentry_aud': 'freie Textantwort' 
    },
    Visuell: {
      'ba_vis_01_1': 'Bewegungen',
      'ba_vis_01_2': 'Muster',
      'ba_vis_01_3': 'Spiegelungen',
      'ba_vis_01_4': 'helles Licht',
      'ba_vis_01_5': 'künstliches Licht',
      'ba_vis_01_6': 'Farben',
      'textentry_vis': 'freie Textantwort'
    },
    Taktil: {
      'ba_tak_01_1': 'Vibration/Erschütterung',
      'ba_tak_01_2': 'Temperatur',
      'ba_tak_01_3': 'Wind',
      'ba_tak_01_4': 'Persönliche Distanz/Nähe',
      'ba_tak_01_5': 'Berührungsempfinden',
      'textentry_taktil': 'freie Textantwort' 
    },
    Geruch_Geschmack: {
      'ba_ge_01_1': 'Geruch',
      'ba_ge_01_2': 'Geschmack',
      'textentry_geruch': 'freie Textantwort'
    },
    Unvorhersehbarkeit: {
      'ba_unp_01_1': 'Neues/Unbekanntes',
      'ba_unp_01_2': 'Raumwechsel',
      'ba_unp_01_3': 'anderes Aussehen',
      'ba_unp_01_4': 'Ordnung nicht eingehalten',
      'textentry_unp': 'freie Textantwort'
    },
    Sonstiges: {
      'ba_so_01_1': 'andere Kinder oder Lehrkräfte',
      'ba_so_01_2': 'Zeitdruck',
      'ba_so_01_3': 'zu viel gleichzeitig los',
      'ba_so_01_4': 'Bodenbeschaffenheit',
      'ba_so_01_5': 'unterbrochene Handlungen',
      'textentry_so': 'freie Textantwort'
    }
  };

  // Function to generate color variations based on main category color
  const getSubcategoryColors = (mainCategory, count) => {
    const baseColors = {
      Auditiv: '#F29400',
      Geruch_Geschmack: '#005B9A',
      Taktil: '#EB6A27',
      Visuell: '#AF122C',
      Unvorhersehbarkeit: '#37A42C',
      Sonstiges: '#646567'
    };
    
    const baseColor = baseColors[mainCategory];
    const colors = [];
    
    // Convert hex to RGB
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : { r: 0, g: 0, b: 0 };
    };
    
    // Convert RGB to hex
    const rgbToHex = (r, g, b) => {
      return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
    };
    
    const rgb = hexToRgb(baseColor);
    
    // Generate color variations for each subcategory
    for (let i = 0; i < count; i++) {
      // Create variations by adjusting brightness (darker to lighter)
      const factor = 0.6 + (i * 0.3); // Creates a range from darker to lighter
      const r = Math.min(255, Math.floor(rgb.r * factor));
      const g = Math.min(255, Math.floor(rgb.g * factor));
      const b = Math.min(255, Math.floor(rgb.b * factor));
      
      colors.push(rgbToHex(r, g, b));
    }
    
    return colors;
  };

  // Process data for the specific day and category
  const processDayCategoryData = () => {
    if (!dayData || !category) return [];
    
    const categorySubs = subcategories[category];
    const subcategoryData = [];
    const dayDate = dayData.date;
    
    // Initialize counts for all subcategories
    Object.entries(categorySubs).forEach(([itemId, itemName]) => {
      let count = 0;
      
      // Count responses for this subcategory on this specific day
      responses.forEach(response => {
        // Check if response is from the correct day
        const responseDate = response.session_id.split('_')[1];
        if (responseDate !== dayDate) return;
        
        // Check if it's the correct item
        if (response.item_name === itemId) {
          // Handle textentry items differently
          if (itemId.startsWith('textentry_')) {
            // For textentry items, count if there's any response
            if (response.item_resp !== undefined && response.item_resp !== null && response.item_resp !== '') {
              count += 1;
            }
          } else {
            // Handle both response formats for non-textentry items
            let antwort1, antwort2;
            if (response.item_resp.includes(';')) {
              [antwort2, antwort1] = response.item_resp.split(';');
            } else {
              antwort1 = response.item_resp;
            }
            
            // Only count if Antwort-1 is "1"
            if (antwort1?.trim() === '1' || antwort2?.trim() === '1') {
              count += 1;
            }
          }
        }
      });
      
      subcategoryData.push({
        name: itemName,
        count: count,
        id: itemId
      });
    });
    
    return subcategoryData;
  };

  const chartData = processDayCategoryData();
  const colors = getSubcategoryColors(category, chartData.length);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }} onClick={onClose}>
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        maxWidth: '80%',
        maxHeight: '80%',
        overflow: 'auto',
        minWidth: '500px'
      }} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ marginTop: 0 }}>
          {category.replace('_', '/')} - {dayData.name} ({dayData.date})
        </h2>
        
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="name" 
              angle={-45} 
              textAnchor="end" 
              height={80}
              tick={{ fontSize: 15 }}
            />
            <YAxis 
              allowDecimals={false}
              domain={[0, 1]} // Restrict to only 0 and 1
              ticks={[0, 1]} // Explicitly set ticks to only 0 and 1
              tick={{ fontSize: 15 }}
            />
            
            {/* Use the custom tooltip */}
            <Tooltip 
              content={<CustomTooltip 
                responses={responses} 
                dayDate={dayData.date}
              />}
            />
            
            <Bar dataKey="count">
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={colors[index]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        
        <button 
          onClick={onClose}
          style={{
            marginTop: '20px',
            padding: '10px 20px',
            backgroundColor: '#4a6fa5',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'block',
            marginLeft: 'auto',
            marginRight: 'auto'
          }}
        >
          Schließen
        </button>
      </div>
    </div>
  );
};

export default DayCategoryDetailModal;